import { Role } from '@mai-team-amusement/core';
import { LocaleProvider, MaiApp, SystemLayout, useCommonT } from '@mai-team-amusement/ui';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { i18n } from 'i18next';
import { RouterProvider, createBrowserRouter, type RouteObject } from 'react-router';
import { useReportT } from './i18n/index.js';
import { ReportPage } from './pages/ReportPage.js';

const LOADING_MARGIN = 8;

function Layout() {
  const t = useReportT();
  const common = useCommonT();
  return (
    <SystemLayout
      app={MaiApp.Report}
      navigation={[
        { path: '/', label: t('nav.report'), minRole: Role.Employee },
        { path: '/history', label: t('nav.history'), minRole: Role.Employee },
        { path: '/team', label: t('nav.team'), minRole: Role.Manager },
        { path: '/admin', label: common('system.nav.admin'), minRole: Role.Admin },
      ]}
    />
  );
}

/** Shown while the first page's code loads. */
function RouteLoading() {
  const common = useCommonT();
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: LOADING_MARGIN }}>
      <CircularProgress aria-label={common('status.loading')} />
    </Box>
  );
}

/**
 * The app's routes (also rendered by tests with a memory router). The report page is in the main
 * bundle; the other pages load on first use. A new array per router: resolving a lazy route
 * updates its route object.
 */
export const createRoutes = (): RouteObject[] => [
  {
    path: '/',
    Component: Layout,
    HydrateFallback: RouteLoading,
    children: [
      { index: true, Component: ReportPage },
      { path: 'reports/:sub', Component: ReportPage },
      {
        path: 'history',
        lazy: { Component: async () => (await import('./pages/HistoryPage.js')).HistoryPage },
      },
      {
        path: 'team',
        lazy: { Component: async () => (await import('./pages/TeamPage.js')).TeamPage },
      },
      {
        path: 'admin',
        lazy: { Component: async () => (await import('./pages/AdminPage.js')).AdminPage },
      },
    ],
  },
];

const queryClient = new QueryClient({
  defaultOptions: { queries: { refetchOnWindowFocus: false } },
});

const router = createBrowserRouter(createRoutes());

export function App({ i18n }: { i18n: i18n }) {
  return (
    <QueryClientProvider client={queryClient}>
      <LocaleProvider i18n={i18n}>
        <RouterProvider router={router} />
      </LocaleProvider>
    </QueryClientProvider>
  );
}
